import { Box, FormControl, InputBase, MenuItem, Select, styled } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { useAppSelector } from "../../Common/_hooks/useAppSelector";
import { useAppDispatch } from "../../Common/_hooks/useAppDispatch";
import { useCallback, useMemo } from "react";
import type ProductVariantStore from "../../AppData/_stores/ProductVariantStore";
import type ViewProductStore from "../../AppData/_stores/ViewProductStore";
import { EditActiveVariant } from "../../UIData/_actions/DesignLabStoreActions";

export const InputSelect = styled(InputBase)(({ theme }) => ({
  height: '100%',
  width: '100%',
  color: '#fff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '20px',

  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },

  '&.Mui-focused': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },

  '.MuiInputBase-input:focus': {
    background: 'transparent',
  },

  '.MuiSelect-select.MuiSelect-standard': {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    height: '64px',
    lineHeight: '20px',
    minHeight: '64px',
    minWidth: 0,
    overflow: 'hidden',
    padding: '0px 34px 0px 15px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  '.MuiSelect-select.MuiSelect-standard > *': {
    alignItems: 'center',
    display: 'flex',
    height: '64px',
    lineHeight: '20px',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  '.MuiSelect-icon': {
    color: '#fff',
  },

  [theme.breakpoints.down('md')]: {
    '.MuiSelect-select.MuiSelect-standard': {
      height: 40,
      minHeight: 40,
      padding: '0px 30px 0px 10px',
    },

    '.MuiSelect-select.MuiSelect-standard > *': {
      height: 40,
    },

    '.MuiSelect-icon': {
      right: '8px',
    },
  },
}))

const VariantSelectorFormControl = styled(FormControl)(({ theme }) => ({
  lineHeight: '20px',
  height: '64px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  padding: '0px',
  backgroundColor: 'transparent',
  transition: 'background-color 150ms',

  '.MuiInputBase-root': {
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },

  [theme.breakpoints.down('md')]: {
    flex: '1 1 0',
    height: 40,
  },
}))

const SelectedVariantValue = styled('span')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  height: 64,
  lineHeight: '20px',
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',

  [theme.breakpoints.down('md')]: {
    height: 40,
  },
}))

const SelectedVariantLabel = styled('span')({
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const Separator = styled('div')(({ theme }) => ({
  alignSelf: 'center',
  background: 'rgba(255, 255, 255, 0.35)',
  flexShrink: 0,
  height: 36,
  width: 1,

  [theme.breakpoints.down('md')]: {
    height: 28,
  },
}))

interface Props {
  previewVariantByProductId: Record<number, string>
  selectedViewProduct?: ViewProductStore
  onPreviewVariantChange: (productId: number, variant: string) => void
}

export default function VariantSelector(props: Props) {
  const dispatch = useAppDispatch()
  const { onPreviewVariantChange, previewVariantByProductId } = props;

  const activeProductId = useAppSelector(state => state.get('UIData').get('designLab').get('activeProductId'));
  const activeVariant = useAppSelector(state => state.get('UIData').get('designLab').get('activeVariant'));
  const selectedProductId = props.selectedViewProduct?.get('id_product') ?? activeProductId;
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(selectedProductId)));
  const selectedVariant = useMemo(() => {
    if (!productData) {
      return activeVariant;
    }

    const firstVariant = productData.get('variants').first()?.get('slug') ?? activeVariant;

    if (selectedProductId !== null && selectedProductId !== activeProductId) {
      const previewVariant = previewVariantByProductId[selectedProductId];
      const variant = productData.get('variants').find(variant => variant.get('slug') === previewVariant);

      return variant?.get('slug') ?? firstVariant;
    }

    const variant = productData.get('variants').find(variant => variant.get('slug') === activeVariant);
    return variant?.get('slug') ?? firstVariant;
  }, [activeProductId, activeVariant, previewVariantByProductId, productData, selectedProductId])

  const onChangeVariant = useCallback((e: SelectChangeEvent) => {
    const variant = String(e.target.value);

    if (selectedProductId !== null && selectedProductId !== activeProductId) {
      onPreviewVariantChange(selectedProductId, variant);
      return;
    }

    dispatch(EditActiveVariant(variant));
  }, [activeProductId, dispatch, onPreviewVariantChange, selectedProductId])

  const renderVariant = useCallback((variant: ProductVariantStore) => {
    return <MenuItem key={variant.get('id')} value={variant.get('slug')}>{variant.get('name')}</MenuItem>
  }, [])

  const renderSelectedVariant = useCallback((variantSlug: unknown) => {
    const selectedVariantSlug = typeof variantSlug === 'string' ? variantSlug : '';
    const variant = productData?.get('variants').find(variant => variant.get('slug') === selectedVariantSlug);

    return <SelectedVariantValue>
      <SelectedVariantLabel>
        {variant?.get('name') ?? selectedVariantSlug}
      </SelectedVariantLabel>
    </SelectedVariantValue>
  }, [productData])

  if (!productData || productData.get('variants').count() === 0) return null;

  return <>
    <Separator aria-hidden />
    <VariantSelectorFormControl variant="standard">
      <Select
        variant="standard"
        input={<InputSelect />}
        value={selectedVariant}
        renderValue={renderSelectedVariant}
        onChange={onChangeVariant}>
        {productData.get('variants').sortBy(variant => variant.get('theorder')).valueSeq().map(renderVariant)}
      </Select>
    </VariantSelectorFormControl>
  </>
}
